import { useState, useEffect, useMemo } from 'react'
import { useUIv2 } from '@/hooks/useUIv2'
import { ExtractionsTabLegacy } from './ExtractionsTabLegacy'
import { Table, Copy, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/service/api'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { markdownComponents } from '@/config/markdownConfig'
import { useTranslation } from '@/i18n'
import type { DocumentVisualElements, VisualElementSummary } from '@/types/visual-elements'
import type { ExtractedTablesResponse, ExtractedTable } from '@/types/extracted-tables'

export interface ExtractionsTabProps {
  pageText: string | null
  documentId: string
  currentPage?: number
  onElementClick?: (pageNumber: number) => void
  documentStatus?: string
  /** Search query from parent sub-toolbar */
  searchQuery?: string
}

// Parse markdown tables from text
function parseMarkdownTables(markdown: string): Array<{ content: string; index: number }> {
  if (!markdown) return []

  const tables: Array<{ content: string; index: number }> = []
  const lines = markdown.split('\n')
  let currentTable: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.trim().includes('|')) {
      currentTable.push(line)
    } else if (currentTable.length > 0) {
      const tableContent = currentTable.join('\n')
      if (tableContent.includes('---')) {
        tables.push({ content: tableContent, index: tables.length })
      }
      currentTable = []
    }
  }

  if (currentTable.length > 0) {
    const tableContent = currentTable.join('\n')
    if (tableContent.includes('---')) {
      tables.push({ content: tableContent, index: tables.length })
    }
  }

  return tables
}

// ─── Table card for structured tables (matching Figma design) ───

interface StructuredTableCardProps {
  table: ExtractedTable
  index: number
}

const StructuredTableCard: React.FC<StructuredTableCardProps> = ({ table, index }) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const confidencePercent = Math.round(table.confidence * 100)
  const confidenceColor = table.confidence >= 0.8
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
    : table.confidence >= 0.5
      ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'
      : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'

  const handleCopy = () => {
    // Build plaintext table for clipboard
    const lines: string[] = []
    if (table.headers && table.headers.length > 0) {
      lines.push(table.headers.join('\t'))
    }
    for (const row of table.rows) {
      lines.push(row.map(c => c || '').join('\t'))
    }
    navigator.clipboard.writeText(lines.join('\n'))
    setCopied(true)
    toast.success(t('document_viewer:extractions.toastCopied', { defaultValue: 'Copied to clipboard' }))
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="border-b border-[#e5e5e5] dark:border-dark-border last:border-b-0">
      {/* Header row with confidence + dimensions */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <Badge className={cn('text-xs border', confidenceColor)}>
          {t('document_viewer:extractions.confidence', { percent: confidencePercent, defaultValue: '{{percent}}% Confidence' })}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {table.num_rows} &times; {table.num_columns}
        </span>
      </div>

      {/* Table title + copy button */}
      <div className="flex items-center justify-between px-4 pb-2">
        <h3 className="text-base font-semibold text-foreground">
          {table.title || t('document_viewer:extractions.tableN', { number: index + 1, defaultValue: 'Table {{number}}' })}
        </h3>
        <button
          type="button"
          onClick={handleCopy}
          className="size-7 flex items-center justify-center rounded hover:bg-muted/50 transition-colors"
          title={t('document_viewer:extractions.copyTable', { defaultValue: 'Copy table' })}
        >
          {copied ? (
            <Check className="size-4 text-emerald-600" />
          ) : (
            <Copy className="size-4 text-muted-foreground" />
          )}
        </button>
      </div>

      {/* Table grid */}
      <div className="px-4 pb-4 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          {table.headers && table.headers.length > 0 && (
            <thead>
              <tr>
                {table.headers.map((header, hi) => (
                  <th
                    key={hi}
                    className="text-left p-2 bg-gray-50 dark:bg-dark-muted border border-[#e5e5e5] dark:border-dark-border font-medium text-foreground"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {table.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="p-2 border border-[#e5e5e5] dark:border-dark-border text-foreground align-top"
                  >
                    {cell || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Caption */}
      {table.caption && (
        <p className="px-4 pb-3 text-xs text-muted-foreground italic">{table.caption}</p>
      )}
    </div>
  )
}

// ─── Visual table card ───

interface TableElementCardProps {
  element: VisualElementSummary
  onClick?: (pageNumber: number) => void
}

const VisualTableCard: React.FC<TableElementCardProps> = ({ element, onClick }) => {
  const { t } = useTranslation()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    let blobUrl: string | null = null
    let isCancelled = false

    const loadImage = async () => {
      if (!element.has_image) return
      try {
        const blob = await apiService.getVisualElementImage(element.id, false)
        if (!isCancelled) {
          blobUrl = URL.createObjectURL(blob)
          setImageUrl(blobUrl)
          setImageError(false)
        }
      } catch (error) {
        if (!isCancelled) {
          console.error(`Failed to load image for element ${element.id}:`, error)
          setImageError(true)
        }
      }
    }

    loadImage()
    return () => {
      isCancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [element.id, element.has_image])

  return (
    <div
      className="p-4 border-b border-[#e5e5e5] dark:border-dark-border cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={() => onClick?.(element.page_number)}
    >
      <div className="flex items-center gap-2 mb-2">
        <Table className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{t('document_viewer:extractions.visualTable', { defaultValue: 'Visual Table' })}</span>
        <Badge variant="secondary" className="text-xs">{t('document_viewer:extractions.page', { number: element.page_number, defaultValue: 'Page {{number}}' })}</Badge>
      </div>

      {element.has_image && (
        <div className="mb-2 bg-white dark:bg-dark-card rounded border dark:border-dark-border overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={element.caption || t('document_viewer:extractions.tableOnPage', { number: element.page_number, defaultValue: 'Table on page {{number}}' })}
              className="w-full h-auto object-contain p-2"
            />
          ) : imageError ? (
            <div className="w-full h-32 flex items-center justify-center text-muted-foreground text-sm">
              {t('document_viewer:extractions.imageLoadFailed', { defaultValue: 'Failed to load image' })}
            </div>
          ) : (
            <div className="w-full h-32 flex items-center justify-center text-muted-foreground text-sm">
              {t('document_viewer:extractions.loadingShort', { defaultValue: 'Loading...' })}
            </div>
          )}
        </div>
      )}

      {element.caption && (
        <p className="text-sm text-muted-foreground">{element.caption}</p>
      )}
    </div>
  )
}

// ─── Markdown table card ───

interface MarkdownTableCardProps {
  table: { content: string; index: number }
}

const MarkdownTableCard: React.FC<MarkdownTableCardProps> = ({ table }) => {
  const { t } = useTranslation()
  return (
    <div className="p-4 border-b border-[#e5e5e5] dark:border-dark-border">
      <div className="flex items-center gap-2 mb-2">
        <Table className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{t('document_viewer:extractions.tableN', { number: table.index + 1, defaultValue: 'Table {{number}}' })}</span>
      </div>
      <div className="prose prose-sm max-w-none overflow-x-auto">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {table.content}
        </ReactMarkdown>
      </div>
    </div>
  )
}

// ─── Main ExtractionsTab ───

function ExtractionsTabV2({ pageText, documentId, currentPage, onElementClick, searchQuery }: ExtractionsTabProps) {
  const { t } = useTranslation()
  const [visualElements, setVisualElements] = useState<DocumentVisualElements | null>(null)
  const [structuredTables, setStructuredTables] = useState<ExtractedTablesResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const markdownTables = useMemo(() => parseMarkdownTables(pageText || ''), [pageText])

  useEffect(() => {
    if (!documentId) return

    const loadData = async () => {
      setLoading(true)
      try {
        const [visualData, tablesData] = await Promise.all([
          apiService.getDocumentVisualElements(documentId).catch(err => {
            console.error('Failed to load visual elements:', err)
            return null
          }),
          apiService.getDocumentExtractedTables(documentId).catch(err => {
            console.error('Failed to load structured tables:', err)
            return null
          })
        ])
        setVisualElements(visualData)
        setStructuredTables(tablesData)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [documentId])

  // Apply search filter + current page filter
  const searchTerm = searchQuery || ''

  const filteredVisualTables = useMemo(() => {
    if (!visualElements?.tables) return []
    let tables = visualElements.tables
    // Filter to current page only
    if (currentPage) tables = tables.filter(t => t.page_number === currentPage)
    if (!searchTerm) return tables
    const s = searchTerm.toLowerCase()
    return tables.filter(t => t.caption?.toLowerCase().includes(s))
  }, [visualElements, searchTerm, currentPage])

  const filteredStructuredTables = useMemo(() => {
    if (!structuredTables?.tables) return []
    let tables = structuredTables.tables
    // Filter to current page only
    if (currentPage) tables = tables.filter(t => t.page_number === currentPage)
    if (!searchTerm) return tables
    const s = searchTerm.toLowerCase()
    return tables.filter(t =>
      t.title?.toLowerCase().includes(s) ||
      t.caption?.toLowerCase().includes(s) ||
      t.rows.some(row => row.some(cell => cell?.toLowerCase()?.includes(s)))
    )
  }, [structuredTables, searchTerm, currentPage])

  const filteredMarkdownTables = useMemo(() => {
    if (!markdownTables.length) return []
    if (!searchTerm) return markdownTables
    const s = searchTerm.toLowerCase()
    return markdownTables.filter(t => t.content.toLowerCase().includes(s))
  }, [markdownTables, searchTerm])

  const hasStructuredTables = filteredStructuredTables.length > 0
  const hasVisualTables = filteredVisualTables.length > 0
  const hasMarkdownTables = filteredMarkdownTables.length > 0

  if (loading && !hasStructuredTables && !hasMarkdownTables) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-pulse">
            <Table className="size-8 text-muted-foreground/50" />
          </div>
          <p className="text-sm">{t('document_viewer:extractions.loadingTables', { defaultValue: 'Loading tables...' })}</p>
        </div>
      </div>
    )
  }

  if (!hasVisualTables && !hasMarkdownTables && !hasStructuredTables) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <Table className="size-8 text-muted-foreground/30" />
          <p className="text-sm">{t('document_viewer:extractions.noTables', { defaultValue: 'No tables found in this document.' })}</p>
          <p className="text-xs text-muted-foreground/70">
            {t('document_viewer:extractions.noTablesHint', { defaultValue: 'Tables will appear here once extracted.' })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-dark-card">
      {/* Structured tables — primary, matching Figma grid style */}
      {hasStructuredTables && filteredStructuredTables.map((table, idx) => (
        <StructuredTableCard key={table.table_id} table={table} index={idx} />
      ))}

      {/* Markdown tables (fallback when no structured tables) */}
      {hasMarkdownTables && !hasStructuredTables && filteredMarkdownTables.map((table) => (
        <MarkdownTableCard key={table.index} table={table} />
      ))}

      {/* Visual tables (AI-detected, fallback) */}
      {hasVisualTables && !hasStructuredTables && filteredVisualTables.map((table) => (
        <VisualTableCard key={table.id} element={table} onClick={onElementClick} />
      ))}
    </div>
  )
}

// Gated on the uiv2 flag (#7537): prod keeps the legacy ExtractionsTab until the flag
// flips; the V2 render only shows when useUIv2() is true. Addresses @amjibaly.
export function ExtractionsTab(props: ExtractionsTabProps) {
  const isV2 = useUIv2()
  return isV2 ? <ExtractionsTabV2 {...props} /> : <ExtractionsTabLegacy {...props} />
}
